import {
  ButtonDS,
  DialogModalDS,
  DownloadOutlined,
  Spacer,
  TextCapitalized,
  dateFormatterDayMonthYearLong2Digits,
  dateFormatterHourMinute,
  uppercaseFirstLetter,
} from "@qivia/ui";
import { createFileXlsx } from "@qivia/ui/src/designSystem/components/materialUiTable/createFileXlsx";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import styled from "styled-components";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import { useAppDispatch } from "../../../../redux/hooks";

export type VehicleHistoryEntry = {
  vehicleUuid: string;
  startOfUsage: string;
  endOfUsage: string | null;
  vehicle?: {
    registrationNumber: string | null;
    reference: string | null;
  };
};

type VehicleHistoryRow = {
  registrationNumber: string;
  vehicleReference: string;
  startOfUsage: string;
  startOfUsageTime: string;
  endOfUsage: string;
  endOfUsageTime: string;
};

export const VehicleHistoryModal = (props: {
  visible: boolean;
  onClose: () => void;
  driverHistory: VehicleHistoryEntry[];
  driverName: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const vehicleHistoryExcelColumns = useMemo(
    () =>
      [
        "registrationNumber",
        "vehicleReference",
        "startOfUsage",
        "startOfUsageTime",
        "endOfUsage",
        "endOfUsageTime",
      ] as (keyof VehicleHistoryRow)[],
    [],
  );

  const vehicleHistoryRows = useMemo(
    () =>
      props.driverHistory.map((dh) => ({
        registrationNumber: dh.vehicle?.registrationNumber ?? "",
        vehicleReference: dh.vehicle?.reference ?? "",
        startOfUsage: dateFormatterDayMonthYearLong2Digits(
          new Date(dh.startOfUsage),
        ),
        startOfUsageTime: dateFormatterHourMinute(new Date(dh.startOfUsage)),
        endOfUsage: dh.endOfUsage
          ? dateFormatterDayMonthYearLong2Digits(new Date(dh.endOfUsage))
          : "",
        endOfUsageTime: dh.endOfUsage
          ? dateFormatterHourMinute(new Date(dh.endOfUsage))
          : "",
      })),
    [props.driverHistory],
  );

  const handleDownloadVehicleHistory = () => {
    if (vehicleHistoryRows.length <= 0) return;
    const headerTitles = vehicleHistoryExcelColumns.map((key) =>
      uppercaseFirstLetter(t(`driversHistory.column.${key}`)),
    );

    const file = createFileXlsx({
      tabTitle: t("drivers.view.title"),
      headerTitles,
      body: vehicleHistoryRows.map((row) =>
        vehicleHistoryExcelColumns.map((column) => row[column]),
      ),
    });

    if (!file) return;
    void dispatch(
      documentDownloadXlsxAsync({
        buffer: file,
        fileName:
          "QIVIA_" +
          t("drivers.sidepanel.driversHistory.excel.title", {
            firstName: props.driverName.split(" ")[0],
            lastName: props.driverName.split(" ")[1],
          }) +
          ".xlsx",
      }),
    );
  };

  return (
    <DialogModalDS
      visible={props.visible}
      onClose={props.onClose}
      title={t("drivers.sidePanel.driversHistory")}
      width={72}
    >
      <StyledModalContent>
        <StyledTableWrapper>
          <StyledTable>
            <thead>
              <tr>
                {vehicleHistoryExcelColumns.map((column) => (
                  <th key={column}>
                    <TextCapitalized>
                      {t(`driversHistory.column.${column}`)}
                    </TextCapitalized>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vehicleHistoryRows.map((row, index) => (
                <tr
                  key={`${row.registrationNumber}-${row.startOfUsage}-${index}`}
                >
                  {vehicleHistoryExcelColumns.map((column) => (
                    <td key={column}>{row[column]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </StyledTableWrapper>
        <Spacer y={1.5} />
        <ButtonDS
          sizeButton="S"
          format="fill"
          buttonType="secondary"
          text={t("drivers.sidepanel.downloadDriversHistoryList")}
          leftIcon={<DownloadOutlined />}
          onClick={handleDownloadVehicleHistory}
        />
      </StyledModalContent>
    </DialogModalDS>
  );
};

const StyledModalContent = styled.div`
  width: 100%;
`;

const StyledTableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  ${typographies["Body/S"]}
  color: ${colors["colors/text/black"]};

  th,
  td {
    border: 0.0625rem solid ${colors["colors/borders/cells/cells"]};
    padding: 0.75rem;
    text-align: left;
  }

  th {
    color: ${colors["colors/text/darkGrey"]};
    font-weight: 600;
  }
`;
